import { isAxiosError } from 'axios';
import { create } from 'zustand';

import type { Asset, Folder, PaginationMeta } from '@/components/asset-manager/types';
import { isImageAsset, isVideoAsset } from '@/components/asset-manager/utils';
import type { AssetFileTypes } from '@/lib/assets-config';
import http from '@/lib/http';
import { cmsAssetPath, cmsAssetsPath } from '@/lib/routes';

function filterParamsForFileTypes(fileTypes: AssetFileTypes): Record<string, string> {
  if (fileTypes === 'images') return { filter: 'images' };
  if (fileTypes === 'videos') return { filter: 'videos' };
  return {};
}

interface AssetsStore {
  assets: Asset[];
  folders: Folder[];
  assetsByFolder: Record<string, Asset[]>;
  folderPagination: Record<string, PaginationMeta | null>;
  loadedFolderFileTypes: Record<string, AssetFileTypes>;
  singleAssets: Record<string, Asset>;
  pagination: PaginationMeta | null;
  isLoading: boolean;
  isLoadingMore: boolean;
  hasLoaded: boolean;
  loadingFolderId: string | null;
  loadedFileTypes: AssetFileTypes | null;
  error: string | null;

  /**
   * Load filtered assets from the API (page 1).
   * Uses cached data for the same fileTypes filter unless force=true.
   */
  loadAssets: (fileTypes?: AssetFileTypes, force?: boolean) => Promise<Asset[]>;

  /**
   * Load assets for a specific folder (page 1), respecting fileTypes filter.
   * Uses cached data if already loaded for the same fileTypes. Pass force=true to refetch.
   */
  loadFolderAssets: (
    folderId: string,
    fileTypes?: AssetFileTypes,
    force?: boolean,
  ) => Promise<Asset[]>;

  /**
   * Load a single asset by ID (e.g. for pre-selecting an asset not yet in the paginated list).
   * Uses cached data if already loaded.
   */
  loadSingleAsset: (assetId: string) => Promise<Asset | null>;

  clearError: () => void;

  /** Fetch and append the next page. Pass folderId to paginate within a folder, omit for root. */
  loadMore: (folderId?: string) => Promise<void>;

  markStale: () => void;
  reset: () => void;
}

export const useAssetsStore = create<AssetsStore>((set, get) => ({
  assets: [],
  folders: [],
  assetsByFolder: {},
  folderPagination: {},
  loadedFolderFileTypes: {},
  singleAssets: {},
  pagination: null,
  isLoading: false,
  isLoadingMore: false,
  hasLoaded: false,
  loadingFolderId: null,
  loadedFileTypes: null,
  error: null,

  loadAssets: async (fileTypes = 'images', force = false) => {
    const { hasLoaded, isLoading, assets, loadedFileTypes } = get();

    if (hasLoaded && loadedFileTypes === fileTypes && !force) return assets;
    if (isLoading) return assets;

    set({ isLoading: true, error: null });

    try {
      const response = await http.get(cmsAssetsPath({ format: 'json' }), {
        params: filterParamsForFileTypes(fileTypes),
      });
      const loadedAssets: Asset[] = response.data.assets || [];
      const folders: Folder[] = response.data.folders || [];
      const pagination: PaginationMeta | null = response.data.pagination ?? null;

      set({
        assets: loadedAssets,
        folders,
        pagination,
        isLoading: false,
        hasLoaded: true,
        loadedFileTypes: fileTypes,
        error: null,
      });

      return loadedAssets;
    } catch (err) {
      console.error('Failed to fetch assets:', err);
      const noun = fileTypes === 'videos' ? 'videos' : fileTypes === 'all' ? 'assets' : 'images';
      set({ isLoading: false, error: `Failed to load ${noun}. Please try again.` });
      return [];
    }
  },

  loadFolderAssets: async (
    folderId: string,
    fileTypes: AssetFileTypes = 'images',
    force = false,
  ) => {
    const { assetsByFolder, loadingFolderId, loadedFolderFileTypes } = get();

    if (!force && folderId in assetsByFolder && loadedFolderFileTypes[folderId] === fileTypes) {
      return assetsByFolder[folderId];
    }
    if (loadingFolderId === folderId) return assetsByFolder[folderId] ?? [];

    set({ loadingFolderId: folderId, error: null });

    try {
      const response = await http.get(cmsAssetsPath({ format: 'json', folderId }), {
        params: filterParamsForFileTypes(fileTypes),
      });
      const folderAssets = (response.data.assets || []).filter((a: Asset) => {
        if (fileTypes === 'images') return isImageAsset(a);
        if (fileTypes === 'videos') return isVideoAsset(a);
        return true;
      });
      const pagination: PaginationMeta | null = response.data.pagination ?? null;

      set((state) => ({
        assetsByFolder: { ...state.assetsByFolder, [folderId]: folderAssets },
        folderPagination: { ...state.folderPagination, [folderId]: pagination },
        loadedFolderFileTypes: { ...state.loadedFolderFileTypes, [folderId]: fileTypes },
        loadingFolderId: null,
        error: null,
      }));

      return folderAssets;
    } catch (err) {
      console.error('Failed to fetch assets for folder:', folderId, err);
      const noun = fileTypes === 'videos' ? 'videos' : fileTypes === 'all' ? 'assets' : 'images';
      set({
        loadingFolderId: null,
        error: `Failed to load ${noun} for this folder. Please try again.`,
      });
      return [];
    }
  },

  loadSingleAsset: async (assetId: string) => {
    const { singleAssets } = get();
    if (singleAssets[assetId]) return singleAssets[assetId];

    try {
      const response = await http.get(cmsAssetPath(assetId, { format: 'json' }));
      const asset: Asset = response.data;
      set((state) => ({ singleAssets: { ...state.singleAssets, [assetId]: asset } }));
      return asset;
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 404) return null;

      console.error('Failed to fetch asset:', assetId, err);
      return null;
    }
  },

  clearError: () => {
    set({ error: null });
  },

  loadMore: async (folderId?: string) => {
    const state = get();
    const pagination = folderId ? state.folderPagination[folderId] : state.pagination;

    if (!pagination || pagination.currentPage >= pagination.totalPages) return;
    if (state.isLoading || state.isLoadingMore) return;
    if (!folderId && state.loadedFileTypes == null) return;

    set({ isLoadingMore: true });

    const folderFileTypes = folderId ? (state.loadedFolderFileTypes[folderId] ?? 'images') : null;

    try {
      const nextPage = pagination.currentPage + 1;
      const response = await http.get(
        cmsAssetsPath({ format: 'json', ...(folderId ? { folderId } : {}) }),
        {
          params: {
            page: nextPage,
            ...(folderId
              ? filterParamsForFileTypes(folderFileTypes ?? 'images')
              : filterParamsForFileTypes(state.loadedFileTypes ?? 'images')),
          },
        },
      );
      const newAssets: Asset[] = (response.data.assets || []).filter((a: Asset) => {
        if (!folderId) return true;
        if (folderFileTypes === 'images') return isImageAsset(a);
        if (folderFileTypes === 'videos') return isVideoAsset(a);
        return true;
      });
      const newPagination: PaginationMeta | null = response.data.pagination ?? null;

      if (folderId) {
        set((s) => ({
          assetsByFolder: {
            ...s.assetsByFolder,
            [folderId]: [...(s.assetsByFolder[folderId] ?? []), ...newAssets],
          },
          folderPagination: { ...s.folderPagination, [folderId]: newPagination },
          isLoadingMore: false,
        }));
      } else {
        set((s) => ({
          assets: [...s.assets, ...newAssets],
          pagination: newPagination,
          isLoadingMore: false,
        }));
      }
    } catch (err) {
      console.error('Failed to load more assets:', err);
      set({ isLoadingMore: false });
    }
  },

  markStale: () => {
    set({
      hasLoaded: false,
      folders: [],
      assets: [],
      assetsByFolder: {},
      folderPagination: {},
      loadedFolderFileTypes: {},
      singleAssets: {},
      loadingFolderId: null,
      loadedFileTypes: null,
      error: null,
    });
  },

  reset: () => {
    set({
      assets: [],
      folders: [],
      assetsByFolder: {},
      folderPagination: {},
      loadedFolderFileTypes: {},
      singleAssets: {},
      pagination: null,
      isLoading: false,
      isLoadingMore: false,
      hasLoaded: false,
      loadingFolderId: null,
      loadedFileTypes: null,
      error: null,
    });
  },
}));
